import { useEffect, useState, useMemo } from 'react'
import {
  useRecoilValue,
  useRecoilCallback,
  useSetRecoilState,
  useRecoilState,
} from 'recoil'
import { alignmentPointsAtom } from '@/atoms/_editorAtoms'
import {
  pageZoomAtom,
  pageExceedsCanvasAtom,
  itemIdAtom,
  watermarkAtomFamily,
  watermarkIdAtom,
  pageLayoutAtom,
  rightMenuAtom,
  themeOverrideAtom,
  themeIdAtom,
  outsideCanvasItemsVisibleAtom,
  selectedItemsAtom,
} from 'atoms'
import Flex from 'components/priority/Flex/Flex'
import { BoundingBox, Item, PageLoading, BackgroundDrag, GrayoutZone } from './'
import { ItemControlOverlay } from '@/components/page/Items/ItemControlOverlay/ItemControlOverlay'
import PageItemGuard from '@/components/page/Items/PageItemGuard'
import Divider from './Divider/Divider'
import { ProductBleed } from './ProductBleed/ProductBleed'
import { VerticalAlignment } from './Alignment'
import { Rulers } from './Rulers'
import * as S from './Page.styled'
import { camelToSnakeCase } from 'utils'
import useColors from 'hooks/utility/useColors'
import { countEditorProbeRender } from '@/utils/editor/editorProbe'
import { useEditorProbeLifetime } from '@/hooks/utility/useEditorProbeLifetime'
import { normalizeBackgroundLayout } from '@/utils/editor/backgroundUtils'
import { useHydrateCanvasFromPage } from '@/hooks/editor/useHydrateCanvasFromPage'
import { pageCenterAndFoldMarginsOf } from '@/utils/editor/layoutMargins'
import { useFillCaseImages } from 'hooks'
import { caseImagesAtom } from '@/atoms/caseImageAtoms'

export const Page = ({
  children,
  pageRef,
  page: { product, pageProperties },
  theme,
  isPMRegisterBook,
  isEven,
  baseLayout,
  ...props
}) => {
  countEditorProbeRender('Page')
  useEditorProbeLifetime('Page')
  const zoom = useRecoilValue(pageZoomAtom)
  const pageExceedsCanvas = useRecoilValue(pageExceedsCanvasAtom)
  const itemIds = useRecoilValue(itemIdAtom)
  const watermarkIds = useRecoilValue(watermarkIdAtom)
  const setAlignmentPoints = useSetRecoilState(alignmentPointsAtom)
  const [pageLayout, setPageLayout] = useRecoilState(pageLayoutAtom)
  const activeMenu = useRecoilValue(rightMenuAtom)
  const selectedItems = useRecoilValue(selectedItemsAtom)
  const hasMultiSelected = selectedItems.length > 1
  const themeOverride = useRecoilValue(themeOverrideAtom)
  const themeId = useRecoilValue(themeIdAtom)
  const outsideCanvasItemsVisible = useRecoilValue(
    outsideCanvasItemsVisibleAtom,
  )
  const [showVerticalCenter, setShowVerticalCenter] = useState(false)
  const [showHorizontalCenter, setShowHorizontalCenter] = useState(false)
  const getColor = useColors()
  const caseImages = useRecoilValue(caseImagesAtom)
  const fillCaseImages = useFillCaseImages()

  useEffect(() => {
    fillCaseImages()
  }, [caseImages, fillCaseImages])

  useHydrateCanvasFromPage(product)

  const loadWatermarkData = useRecoilCallback(({ set }) => (watermarks) => {
    const ids = []
    for (const item of watermarks) {
      ids.push(item.id)
      set(watermarkAtomFamily(item.id), item)
    }
    set(watermarkIdAtom, ids)
  })

  const normalizedThemeOverride = useMemo(
    () => normalizeBackgroundLayout(themeOverride, null),
    [themeOverride],
  )

  useEffect(() => {
    // TODO: add these strings to consts once consts PR is merged
    const isVerticalCenter = baseLayout?.fold_line === 'vertical-center'
    const isHorizontalCenter = baseLayout?.fold_line === 'horizontal-center'
    setShowVerticalCenter(isVerticalCenter)
    setShowHorizontalCenter(isHorizontalCenter)
  }, [baseLayout])

  useEffect(() => {
    if (product?.watermarks) {
      loadWatermarkData(product?.watermarks)
    }
    // eslint-disable-next-line
  }, [product?.watermarks])

  useEffect(() => {
    setAlignmentPoints(product?.alignmentPoints)
    // eslint-disable-next-line
  }, [product?.alignmentPoints])

  // Sync page layout with theme and page-specific overrides.
  // Normalizes background properties to ensure consistent defaults (background_fill, top, left, opacity).
  // If page has custom background settings from a different theme -> use current theme defaults.
  // If page has custom background settings matching current theme -> merge page settings with theme.
  // If page has no custom background settings -> use current theme defaults.
  useEffect(() => {
    const pageThemeOverride = camelToSnakeCase(product?.themeOverride)
    const pageHasNoThemeOverride = !pageThemeOverride
    const pageThemeIdMatchesThemeId =
      pageThemeOverride?.theme_id === (themeId || themeOverride?.theme_id)
    const shouldUsePageOverride =
      !pageHasNoThemeOverride && pageThemeIdMatchesThemeId
    const themeOverrideIsMismatch =
      !pageHasNoThemeOverride && !pageThemeIdMatchesThemeId
    const normalizedPageLayout = shouldUsePageOverride
      ? normalizeBackgroundLayout(themeOverride, pageThemeOverride)
      : normalizedThemeOverride

    if (themeOverrideIsMismatch) {
      const normalizedWithTheme = {
        ...normalizedThemeOverride,
        theme_id: themeOverride?.theme_id,
      }

      setPageLayout(normalizedWithTheme)
      return
    }

    setPageLayout(normalizedPageLayout)
    // eslint-disable-next-line
  }, [product?.themeOverride, themeId, themeOverride, normalizedThemeOverride])

  const editedImageMatchesTheme =
    themeOverride?.theme_id === pageLayout?.background_edits?.theme_id

  // background image url logic
  const backgroundImageUrl =
    pageLayout?.background_edits?.url && editedImageMatchesTheme
      ? pageLayout.background_edits.url
      : `${process.env.REACT_APP_S3BUCKET_THEME_IMAGES}${
          themeOverride?.replacement_image
            ? themeOverride?.replacement_image
            : theme.image
        }`

  const rulerProps = {
    height: product?.productSize?.height,
    width: product?.productSize?.width,
    padding: product?.padding,
    whiteBorder: product?.whiteBorder,
  }

  const grayoutZoneProps = {
    outsideCanvasItemsVisible,
  }

  const centerAndFoldMargins = useMemo(
    () =>
      pageCenterAndFoldMarginsOf(
        baseLayout,
        pageProperties?.pageNumber,
        product?.productSize?.width,
      ),
    [baseLayout, pageProperties?.pageNumber, product?.productSize?.width],
  )

  const productBleedProps = {
    height: product?.productSize?.height,
    width: product?.productSize?.width,
    marginTop: baseLayout?.margin_top,
    marginRight: baseLayout?.margin_right,
    marginBottom: baseLayout?.margin_bottom,
    marginLeft: baseLayout?.margin_left,
    ...centerAndFoldMargins,
    showVerticalCenter,
    showHorizontalCenter,
  }

  if (!product) return null

  return (
    <Flex
      justify={pageExceedsCanvas ? 'flex-start' : 'center'}
      minHeight="100%"
      width="100%"
      data-bounding-box
    >
      <S.Page
        height={`${product?.productSize?.height}px`}
        minWidth={`${product?.productSize?.width}px`}
        padding={product?.padding}
        whiteBorder={product?.whiteBorder}
        zoom={zoom}
        pageExceedsCanvas={pageExceedsCanvas}
        ref={pageRef}
        theme={theme}
        themeOverride={themeOverride}
        backgroundImageUrl={backgroundImageUrl}
        pageLayout={pageLayout?.background_fill ? pageLayout : themeOverride}
        data-bounding-box
        preprinted={pageProperties?.paperType === 'preprinted'}
        backgroundTabActive={activeMenu === 'Background'}
        pageTwo={
          pageProperties?.pageNumber >= 2 &&
          !pageProperties?.product?.includes('Register')
        }
        {...props}
        data-page-surface="editor"
        data-page-id={product?.orderPageId}
        data-page-zoom={zoom}
      >
        <ProductBleed {...productBleedProps} />
        <Rulers {...rulerProps} />
        <GrayoutZone {...grayoutZoneProps} />
        {isPMRegisterBook && <S.RBDiv product={product} isEven={isEven} />}
        <PageLoading />
        <BackgroundDrag
          backgroundImageUrl={backgroundImageUrl}
          pageLayout={pageLayout}
          preprinted={pageProperties?.paperType === 'preprinted'}
          backgroundTabActive={activeMenu === 'Background'}
          pageTwo={
            pageProperties?.pageNumber >= 2 &&
            !pageProperties?.product?.includes('Register')
          }
        />
        {watermarkIds?.map((id) => (
          <PageItemGuard id={id} key={id}>
            <Item
              type="watermark"
              data-bounding-box={activeMenu !== 'Watermarks'}
              id={id}
            />
          </PageItemGuard>
        ))}
        {product?.dividers?.map((divider, index) => (
          <Divider
            key={index}
            left={divider?.left}
            whiteBorder={divider?.whiteBorder}
            padding={product?.padding?.left}
            productBleedBackground={getColor('productBleedBackground', 45)}
            data-bounding-box
          />
        ))}
        {product?.alignmentPoints?.vertical.map((alignment, index) => (
          <VerticalAlignment
            key={index}
            left={alignment}
            height={product?.productSize?.height}
            whiteBorder={product?.whiteBorder}
            data-bounding-box
          />
        ))}
        {itemIds?.map((id) => (
          <PageItemGuard id={id} key={id}>
            <Item
              id={id}
              hasMultiSelected={hasMultiSelected}
              environment="editor"
            />
          </PageItemGuard>
        ))}
        {/* ItemControlOverlay renders selection borders, icons, and tooltips above all items */}
        <ItemControlOverlay />
        <BoundingBox />
        {children}
      </S.Page>
    </Flex>
  )
}

export default Page
